import { useMemo } from "react";
import { useUserContext } from "../../../context/UserContextProvider";
import {
  Customers,
  Dashboard,
  Dot,
  FileBlankIcon,
  InvoiceIcon,
  MenuItemReports,
  MenuItemShoppingBag,
  OrderGeneration2,
  Payments,
  Products,
  Suppliers,
  Transactions,
  TripIcon,
} from "../../icons";
import { AppRoutes } from "../../../constants/routes";
import { usePolicyCheck } from "../../../modules/private/hooks/usePolicyCheck";
import { Policies } from "@fena/toolkit-types";
import { useCompanyStore } from "../../../stores/useCompanyStore";
import { useShowBlockingOverlay } from "./useShowBlockingOverlay";

export const useMenuItems = (
  hasPaymentsOnlySubscription: boolean,
  hasNoSubscription: boolean,
  policies: Map<string, any>,
  ignoreSubscription: boolean,
  hasActiveB2BSubscription: boolean,
  hasToolkitSubscription: boolean,
  canManageCompanies?: boolean,
  cardPaymentsEnabled?: boolean
) => {
  const { checkAccessByPolicies } = usePolicyCheck();
  const { user } = useUserContext();
  const { company, parentCustomizationSettings } = useCompanyStore();
  const { showLockIcon } = useShowBlockingOverlay(
    hasPaymentsOnlySubscription,
    canManageCompanies,
    hasActiveB2BSubscription,
    hasToolkitSubscription,
    company?.b2bSubscription?.subscriptionLevel
  );

  const isPartner =
    company?.managedBy &&
    parentCustomizationSettings?.partner_dashboard?.partnerCustomizationEnabled;

  const isB2bAccount = !!(
    user.selectedCompanyMembership?.role?.name === "B2B" &&
    user.selectedCompanyMembership?.role?.isDefaultRole
  );

  const hasB2bSubscription = !!company?.b2bSubscription;

  const menuItems = useMemo(() => {
    const items = [
      {
        label: "Transactions",
        icon: <Transactions name="transactions" />,
        isActive:
          location.pathname === AppRoutes.Private.Payments.TRANSACTIONS_LIST,
        route: AppRoutes.Private.Payments.TRANSACTIONS_LIST,
        hidden:
          !checkAccessByPolicies(
            [Policies.PAYMENT.ADMIN_ACCESS],
            [Policies.PAYMENT.VIEW_PAYMENTS]
          ) ||
          (!hasPaymentsOnlySubscription && !ignoreSubscription),
      },
      {
        label: "Payments",
        icon: <Payments name="payments" />,
        isActive: [
          AppRoutes.Private.Payments.FENA_PAYMENTS_LIST,
          AppRoutes.Private.Payments.FENA_PAYMENTS_QR,
          AppRoutes.Private.Payments.FENA_PAYMENTS_MOBILE,
          AppRoutes.Private.Payments.PAYMENTS_CASH,
          AppRoutes.Private.Payments.PAYMENTS_CARD,
          AppRoutes.Private.Payments.PAYMENTS_OTHER,
          AppRoutes.Private.Payments.RECURRING_PAYMENTS,
          AppRoutes.Private.Payments.BULK_PAYMENTS_LIST,
          AppRoutes.Private.Payments.BULK_PAYMENTS_RUNS,
          AppRoutes.Private.Payments.BULK_PAYMENTS_UPCOMING,
        ].includes(location.pathname as any),
        defaultOpen: location.pathname.includes("private/payments"),
        hidden:
          !checkAccessByPolicies(
            [Policies.PAYMENT.ADMIN_ACCESS],
            [Policies.PAYMENT.VIEW_PAYMENTS]
          ) ||
          (!hasPaymentsOnlySubscription && !ignoreSubscription),
        subItems: [
          {
            label: isPartner ? "OB Payments" : "Fena OB Payments",
            icon: <Dot name="dot" />,
            isActive:
              location.pathname.includes("private/payments/fena") ||
              location.pathname.includes("private/payments/bulk-payments") ||
              location.pathname.includes("private/payments/recurring"),
            defaultOpen:
              location.pathname.includes("private/payments/fena") ||
              location.pathname.includes("private/payments/bulk-payments") ||
              location.pathname.includes("private/payments/recurring"),
            subItems: [
              {
                label: isPartner ? "Pay by Link" : "Fena Pay by Link",
                icon: <Dot name="dot" />,
                isActive:
                  location.pathname ===
                  AppRoutes.Private.Payments.FENA_PAYMENTS_LIST,
                route: AppRoutes.Private.Payments.FENA_PAYMENTS_LIST,
              },
              {
                label: isPartner ? "QR code" : "Fena QR code",
                icon: <Dot name="dot" />,
                isActive:
                  location.pathname ===
                  AppRoutes.Private.Payments.FENA_PAYMENTS_QR,
                route: AppRoutes.Private.Payments.FENA_PAYMENTS_QR,
              },
              {
                label: "Fena Mobile",
                icon: <Dot name="dot" />,
                isActive: location.pathname.includes(
                  AppRoutes.Private.Payments.FENA_PAYMENTS_MOBILE
                ),
                route: AppRoutes.Private.Payments.FENA_PAYMENTS_MOBILE,
                hidden: isPartner,
              },
              {
                label: "Recurring Payments",
                icon: <Dot name="dot" />,
                isActive: location.pathname.includes(
                  AppRoutes.Private.Payments.RECURRING_PAYMENTS
                ),
                route: AppRoutes.Private.Payments.RECURRING_PAYMENTS,
              },
              {
                label: "Bulk payments",
                icon: <Dot name="dot" />,
                isActive: location.pathname.includes(
                  "private/payments/bulk-payments"
                ),
                defaultOpen: location.pathname.includes(
                  "private/payments/bulk-payments"
                ),
                subItems: [
                  {
                    label: "Payment runs",
                    icon: <Dot name="dot" />,
                    isActive: location.pathname.includes(
                      AppRoutes.Private.Payments.BULK_PAYMENTS_RUNS
                    ),
                    route: AppRoutes.Private.Payments.BULK_PAYMENTS_RUNS,
                  },
                  {
                    label: "Upcoming payments",
                    icon: <Dot name="dot" />,
                    isActive: location.pathname.includes(
                      AppRoutes.Private.Payments.BULK_PAYMENTS_UPCOMING
                    ),
                    route: AppRoutes.Private.Payments.BULK_PAYMENTS_UPCOMING,
                  },
                ],
              },
            ],
          },
          {
            label: "Cash Payments",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Payments.PAYMENTS_CASH
            ),
            route: AppRoutes.Private.Payments.PAYMENTS_CASH,
            hidden: !checkAccessByPolicies(
              [Policies.PAYMENT.ADMIN_ACCESS],
              [Policies.PAYMENT.VIEW_OTHER_PAYMENTS]
            ),
          },
          {
            label: "Card Payments",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Payments.PAYMENTS_CARD
            ),
            route: AppRoutes.Private.Payments.PAYMENTS_CARD,
            hidden: !checkAccessByPolicies(
              [Policies.PAYMENT.ADMIN_ACCESS],
              [Policies.PAYMENT.VIEW_OTHER_PAYMENTS]
            ),
          },
          {
            label: "Fena Checkout",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Payments.PAYMENTS_CHECKOUT
            ),

            route: AppRoutes.Private.Payments.PAYMENTS_CHECKOUT,
            hidden:
              !checkAccessByPolicies(
                [Policies.PAYMENT.ADMIN_ACCESS],
                [Policies.PAYMENT.VIEW_OTHER_PAYMENTS]
              ) || !cardPaymentsEnabled,
          },
          {
            label: "Other Payments",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Payments.PAYMENTS_OTHER
            ),
            route: AppRoutes.Private.Payments.PAYMENTS_OTHER,
            hidden: !checkAccessByPolicies(
              [Policies.PAYMENT.ADMIN_ACCESS],
              [Policies.PAYMENT.VIEW_OTHER_PAYMENTS]
            ),
          },
        ],
      },
      {
        label: "Home",
        icon: <Dashboard name="dashboard" />,
        isActive: location.pathname === AppRoutes.Private.Apps.HOME,
        route: AppRoutes.Private.Apps.HOME,
        hidden: !hasB2bSubscription,
      },
      {
        label: "Reports",
        icon: <MenuItemReports name="reports" />,
        isActive: [AppRoutes.Private.Report.CARD_PAYMENTS].includes(
          location.pathname as any
        ),

        defaultOpen:
          location.pathname.includes("private/reports") &&
          !location.pathname.includes(AppRoutes.Private.Report.CARD_PAYMENTS),
        disabled: showLockIcon(AppRoutes.Private.Report.CARD_PAYMENTS),
        // hasPaymentsOnlySubscription ||
        // hasNoSubscription ||
        // (hasActiveB2BSubscription && !hasToolkitSubscription),
        hidden: !checkAccessByPolicies(
          [Policies.STATISTICS.VIEW_ALL],
          [Policies.STATISTICS.VIEW_PAYMENTS]
        ),
        subItems: [
          {
            label: "Card Payments",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Report.CARD_PAYMENTS
            ),
            route: AppRoutes.Private.Report.CARD_PAYMENTS,
            hidden: !checkAccessByPolicies(
              [Policies.STATISTICS.VIEW_ALL],
              [Policies.STATISTICS.VIEW_PAYMENTS]
            ),
          },
        ],
      },
      {
        label: "Orders",
        icon: <Products name="products" />,
        isActive: location.pathname.includes(
          AppRoutes.Private.Inventory.ORDERS_LIST
        ),
        route: AppRoutes.Private.Inventory.ORDERS_LIST,
        // disabled: isItemDisabled(),
        disabled: showLockIcon(AppRoutes.Private.Inventory.ORDERS_LIST),
        hidden: !checkAccessByPolicies(
          [Policies.ORDER.ADMIN_ACCESS],
          [Policies.ORDER.VIEW_ORDERS]
        ),
      },
      {
        label: "Inventory",
        icon: <MenuItemShoppingBag name="shopping-bag" />,
        isActive:
          [
            AppRoutes.Private.Inventory.PRODUCT_LIST,
            AppRoutes.Private.Inventory.PRODUCT_LIST_STOCK,
            AppRoutes.Private.Inventory.PRODUCT_CREATE,
            AppRoutes.Private.Inventory.PRODUCT_EDIT,
            AppRoutes.Private.Inventory.CATEGORIES,
            AppRoutes.Private.Inventory.BRANDS,
            AppRoutes.Private.Inventory.INCOMING_INVENTORY,
            AppRoutes.Private.Inventory.INCOMING_INVENTORY_NEW_PO,
            AppRoutes.Private.Inventory.INCOMING_INVENTORY_NEW_PO_EDIT,
            AppRoutes.Private.Inventory.INCOMING_INVENTORY_NEW_TRANSFER,
            AppRoutes.Private.Inventory.REJECTED_AND_MISSING,
          ].includes(location.pathname as any) ||
          location.pathname.includes(AppRoutes.Private.Inventory.PRODUCT_EDIT),
        defaultOpen:
          location.pathname.includes("private/inventory") &&
          !location.pathname.includes(AppRoutes.Private.Inventory.ORDERS_LIST),
        // disabled: !!hasPaymentsOnlySubscription || hasNoSubscription,
        disabled: showLockIcon(AppRoutes.Private.Inventory.PRODUCT_LIST),
        hidden: !checkAccessByPolicies(
          [
            Policies.PRODUCT.ADMIN_ACCESS,
            Policies.CATEGORY.ADMIN_ACCESS,
            Policies.BRAND.ADMIN_ACCESS,
            Policies.PRODUCT.VIEW_PRODUCTS,
            Policies.CATEGORY.VIEW_CATEGORIES,
            Policies.BRAND.VIEW_BRANDS,
          ],
          [
            Policies.PRODUCT.VIEW_PRODUCTS,
            Policies.CATEGORY.VIEW_CATEGORIES,
            Policies.BRAND.VIEW_BRANDS,
          ]
        ),
        subItems: [
          {
            label: "Product list",
            icon: <Dot name="dot" />,
            isActive:
              location.pathname.includes(
                AppRoutes.Private.Inventory.PRODUCT_LIST
              ) &&
              !location.pathname.includes(
                AppRoutes.Private.Inventory.PRODUCT_LIST_STOCK
              ),
            route: AppRoutes.Private.Inventory.PRODUCT_LIST,

            hidden: !checkAccessByPolicies(
              [Policies.PRODUCT.ADMIN_ACCESS],
              [Policies.PRODUCT.VIEW_PRODUCTS]
            ),
          },
          {
            label: "Stock",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Inventory.PRODUCT_LIST_STOCK
            ),
            route: AppRoutes.Private.Inventory.PRODUCT_LIST_STOCK,
            disabled:
              hasPaymentsOnlySubscription ||
              hasNoSubscription ||
              (hasActiveB2BSubscription && !hasToolkitSubscription),
            hidden: !checkAccessByPolicies(
              [Policies.PRODUCT.ADMIN_ACCESS],
              [Policies.PRODUCT.VIEW_PRODUCTS]
            ),
          },
          {
            label: "Categories",
            icon: <Dot name="dot" />,
            isActive:
              location.pathname === AppRoutes.Private.Inventory.CATEGORIES,
            route: AppRoutes.Private.Inventory.CATEGORIES,
            hidden: !checkAccessByPolicies(
              [Policies.CATEGORY.ADMIN_ACCESS],
              [Policies.CATEGORY.VIEW_CATEGORIES]
            ),
          },
          {
            label: "Brands",
            icon: <Dot name="dot" />,
            isActive: location.pathname === AppRoutes.Private.Inventory.BRANDS,
            route: AppRoutes.Private.Inventory.BRANDS,
            hidden: !checkAccessByPolicies(
              [Policies.BRAND.ADMIN_ACCESS],
              [Policies.BRAND.VIEW_BRANDS]
            ),
          },
          {
            label: "Incoming inventory",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Inventory.INCOMING_INVENTORY
            ),
            route: AppRoutes.Private.Inventory.INCOMING_INVENTORY,
            hidden: !checkAccessByPolicies(
              [Policies.INCOMING_INVENTORY.ADMIN_ACCESS],
              [Policies.INCOMING_INVENTORY.VIEW_INCOMING_INVENTORY]
            ),
          },
          {
            label: "Rejected and missing",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Inventory.REJECTED_AND_MISSING
            ),
            route: AppRoutes.Private.Inventory.REJECTED_AND_MISSING,
            hidden: !checkAccessByPolicies(
              [Policies.INCOMING_INVENTORY.ADMIN_ACCESS],
              [Policies.INCOMING_INVENTORY.VIEW_INCOMING_INVENTORY]
            ),
          },
        ],
      },
      {
        label: "Order Generation",
        icon: <OrderGeneration2 />,
        isActive: location.pathname.includes("private/order-generation"),
        // disabled: hasPaymentsOnlySubscription || hasNoSubscription,
        disabled: showLockIcon(
          AppRoutes.Private.Inventory.ORDER_GENERATION_EPOS
        ),
        hidden: !checkAccessByPolicies(
          [Policies.ORDER.ADMIN_ACCESS],
          [Policies.ORDER.CREATE_ORDERS]
        ),
        defaultOpen: location.pathname.includes("private/order-generation"),
        subItems: [
          {
            label: "In person",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Inventory.ORDER_GENERATION_EPOS
            ),
            route: AppRoutes.Private.Inventory.ORDER_GENERATION_EPOS,
          },
          {
            label: "Delivery / pick-up",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Inventory.ORDER_GENERATION_NEW_SALES_ORDER
            ),
            route: AppRoutes.Private.Inventory.ORDER_GENERATION_NEW_SALES_ORDER,
          },
        ],
      },
      {
        label: "Suppliers",
        icon: <Suppliers name="suppliers" />,
        route: AppRoutes.Private.Suppliers.SUPPLIERS,
        isActive: location.pathname.includes("suppliers"),
        disabled: showLockIcon(AppRoutes.Private.Suppliers.SUPPLIERS),
        // disabled:
        //   hasPaymentsOnlySubscription ||
        //   hasNoSubscription ||
        //   (hasActiveB2BSubscription && !hasToolkitSubscription),
        hidden: !checkAccessByPolicies(
          [Policies.SUPPLIER.ADMIN_ACCESS],
          [Policies.SUPPLIER.VIEW_SUPPLIERS]
        ),
      },
      {
        label: "Customers",
        icon: <Customers name="customers" />,
        route: AppRoutes.Private.Customers.CUSTOMERS_LIST,
        isActive: location.pathname.includes("customers"),
        disabled: showLockIcon(AppRoutes.Private.Customers.CUSTOMERS_LIST),
        hidden: !checkAccessByPolicies(
          [Policies.CUSTOMER.ADMIN_ACCESS],
          [Policies.CUSTOMER.VIEW_CUSTOMERS]
        ),
      },
      {
        label: "Transactions",
        icon: <Transactions name="transactions" />,
        isActive:
          location.pathname === AppRoutes.Private.Payments.TRANSACTIONS_LIST,
        route: AppRoutes.Private.Payments.TRANSACTIONS_LIST,
        disabled: showLockIcon(AppRoutes.Private.Payments.TRANSACTIONS_LIST),
        // disabled:
        //   hasPaymentsOnlySubscription ||
        //   hasNoSubscription ||
        //   (hasActiveB2BSubscription && !hasToolkitSubscription),
        hidden:
          !checkAccessByPolicies(
            [Policies.PAYMENT.ADMIN_ACCESS],
            [Policies.PAYMENT.VIEW_PAYMENTS]
          ) ||
          hasPaymentsOnlySubscription ||
          ignoreSubscription,
      },
      {
        label: "Payments",
        icon: <Payments name="payments" />,
        isActive: [
          AppRoutes.Private.Payments.FENA_PAYMENTS_LIST,
          AppRoutes.Private.Payments.FENA_PAYMENTS_QR,
          AppRoutes.Private.Payments.FENA_PAYMENTS_MOBILE,
          AppRoutes.Private.Payments.PAYMENTS_CASH,
          AppRoutes.Private.Payments.PAYMENTS_CARD,
          AppRoutes.Private.Payments.PAYMENTS_OTHER,
          AppRoutes.Private.Payments.RECURRING_PAYMENTS,
          AppRoutes.Private.Payments.BULK_PAYMENTS_LIST,
          AppRoutes.Private.Payments.BULK_PAYMENTS_RUNS,
          AppRoutes.Private.Payments.BULK_PAYMENTS_UPCOMING,
        ].includes(location.pathname as any),
        defaultOpen: location.pathname.includes("private/payments"),
        disabled: showLockIcon(AppRoutes.Private.Payments.FENA_PAYMENTS_LIST),
        hidden:
          !checkAccessByPolicies(
            [Policies.PAYMENT.ADMIN_ACCESS],
            [Policies.PAYMENT.VIEW_PAYMENTS]
          ) ||
          hasPaymentsOnlySubscription ||
          ignoreSubscription,
        subItems: [
          {
            label: isPartner ? "OB Payments" : "Fena OB Payments",
            icon: <Dot name="dot" />,
            isActive:
              location.pathname.includes("private/payments/fena") ||
              location.pathname.includes("private/payments/bulk-payments") ||
              location.pathname.includes("private/payments/recurring"),
            defaultOpen:
              location.pathname.includes("private/payments/fena") ||
              location.pathname.includes("private/payments/bulk-payments") ||
              location.pathname.includes("private/payments/recurring"),
            subItems: [
              {
                label: isPartner ? "Pay by Link" : "Fena Pay by Link",
                icon: <Dot name="dot" />,
                isActive:
                  location.pathname ===
                  AppRoutes.Private.Payments.FENA_PAYMENTS_LIST,
                route: AppRoutes.Private.Payments.FENA_PAYMENTS_LIST,
              },
              {
                label: isPartner ? "QR code" : "Fena QR code",
                icon: <Dot name="dot" />,
                isActive:
                  location.pathname ===
                  AppRoutes.Private.Payments.FENA_PAYMENTS_QR,
                route: AppRoutes.Private.Payments.FENA_PAYMENTS_QR,
              },
              {
                label: "Fena Mobile",
                icon: <Dot name="dot" />,
                isActive: location.pathname.includes(
                  AppRoutes.Private.Payments.FENA_PAYMENTS_MOBILE
                ),
                route: AppRoutes.Private.Payments.FENA_PAYMENTS_MOBILE,
                hidden: isPartner,
              },
              {
                label: "Recurring Payments",
                icon: <Dot name="dot" />,
                isActive: location.pathname.includes(
                  AppRoutes.Private.Payments.RECURRING_PAYMENTS
                ),
                route: AppRoutes.Private.Payments.RECURRING_PAYMENTS,
              },
              {
                label: "Bulk payments",
                icon: <Dot name="dot" />,
                isActive: location.pathname.includes(
                  "private/payments/bulk-payments"
                ),
                defaultOpen: location.pathname.includes(
                  "private/payments/bulk-payments"
                ),
                subItems: [
                  {
                    label: "Payment runs",
                    icon: <Dot name="dot" />,
                    isActive: location.pathname.includes(
                      AppRoutes.Private.Payments.BULK_PAYMENTS_RUNS
                    ),
                    route: AppRoutes.Private.Payments.BULK_PAYMENTS_RUNS,
                  },
                  {
                    label: "Upcoming payments",
                    icon: <Dot name="dot" />,
                    isActive: location.pathname.includes(
                      AppRoutes.Private.Payments.BULK_PAYMENTS_UPCOMING
                    ),
                    route: AppRoutes.Private.Payments.BULK_PAYMENTS_UPCOMING,
                  },
                ],
              },
            ],
          },
          {
            label: "Cash Payments",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Payments.PAYMENTS_CASH
            ),
            route: AppRoutes.Private.Payments.PAYMENTS_CASH,
            hidden: !checkAccessByPolicies(
              [Policies.PAYMENT.ADMIN_ACCESS],
              [Policies.PAYMENT.VIEW_OTHER_PAYMENTS]
            ),
          },
          {
            label: "Card Payments",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Payments.PAYMENTS_CARD
            ),
            route: AppRoutes.Private.Payments.PAYMENTS_CARD,
            hidden: !checkAccessByPolicies(
              [Policies.PAYMENT.ADMIN_ACCESS],
              [Policies.PAYMENT.VIEW_OTHER_PAYMENTS]
            ),
          },
          {
            label: "Fena Checkout",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Payments.PAYMENTS_CHECKOUT
            ),

            route: AppRoutes.Private.Payments.PAYMENTS_CHECKOUT,
            hidden:
              !checkAccessByPolicies(
                [Policies.PAYMENT.ADMIN_ACCESS],
                [Policies.PAYMENT.VIEW_OTHER_PAYMENTS]
              ) || !cardPaymentsEnabled,
          },
          {
            label: "Other Payments",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Payments.PAYMENTS_OTHER
            ),
            route: AppRoutes.Private.Payments.PAYMENTS_OTHER,
            hidden: !checkAccessByPolicies(
              [Policies.PAYMENT.ADMIN_ACCESS],
              [Policies.PAYMENT.VIEW_OTHER_PAYMENTS]
            ),
          },
        ],
      },
      {
        label: "Invoices and quotes",
        icon: <InvoiceIcon name="invoices" />,
        isActive:
          location.pathname.includes("private/invoices") ||
          location.pathname.includes("private/quotes"),
        // disabled: isItemDisabled(),
        disabled: showLockIcon(AppRoutes.Private.Invoices.INVOICES_LIST),
        defaultOpen:
          location.pathname.includes("private/invoices") ||
          location.pathname.includes("private/quotes"),
        hidden: !checkAccessByPolicies(
          [Policies.INVOICE.ADMIN_ACCESS],
          [Policies.INVOICE.VIEW_INVOICES]
        ),
        subItems: [
          {
            label: "Invoices",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes("private/invoices"),
            route: AppRoutes.Private.Invoices.INVOICES_LIST,
          },
          {
            label: "Quotes",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes("private/quotes"),
            route: AppRoutes.Private.Invoices.QUOTES_LIST,
          },
        ],
      },
      {
        label: "Commercial invoices",
        defaultOpen: location.pathname.includes("private/commercial-invoices"),
        icon: <FileBlankIcon name="commercial-invoices" />,
        isActive: location.pathname.includes(
          AppRoutes.Private.Invoices.COMMERCIAL_INVOICES_LIST
        ),
        route: AppRoutes.Private.Invoices.COMMERCIAL_INVOICES_LIST,
        // disabled:
        //   hasPaymentsOnlySubscription ||
        //   hasNoSubscription ||
        //   (hasActiveB2BSubscription && !hasToolkitSubscription),
        disabled: showLockIcon(
          AppRoutes.Private.Invoices.COMMERCIAL_INVOICES_LIST
        ),
        hidden: !checkAccessByPolicies(
          [Policies.COMMERCIAL_INVOICES.ADMIN_ACCESS],
          [Policies.COMMERCIAL_INVOICES.VIEW_COMMERCIAL_INVOICES]
        ),
      },
      {
        label: "Shipments",
        icon: <TripIcon />,
        isActive: location.pathname.includes("private/shipments"),
        defaultOpen: location.pathname.includes("private/shipments"),
        // disabled:
        //   hasPaymentsOnlySubscription ||
        //   hasNoSubscription ||
        //   (hasActiveB2BSubscription && !hasToolkitSubscription),
        disabled: showLockIcon(
          AppRoutes.Private.Shipments.SHIPMENTS_PICKING_LIST
        ),
        hidden: !checkAccessByPolicies(
          [
            Policies.WARHOUSE.ADMIN_ACCESS,
            Policies.WARHOUSE.ASSIGN_ORDER_TO_PICKERS,
            Policies.WARHOUSE.VIEW_ORDER_TO_BE_PICKED,
            Policies.WARHOUSE.BOOK_CONSIGNMENTS,
            Policies.WARHOUSE.GENERATE_AND_PRINT_MANIFEST,
          ],
          [
            Policies.WARHOUSE.ASSIGN_ORDER_TO_PICKERS,
            Policies.WARHOUSE.VIEW_ORDER_TO_BE_PICKED,
            Policies.WARHOUSE.BOOK_CONSIGNMENTS,
            Policies.WARHOUSE.GENERATE_AND_PRINT_MANIFEST,
          ]
        ),
        subItems: [
          {
            label: "Picking list",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Shipments.SHIPMENTS_PICKING_LIST
            ),
            route: AppRoutes.Private.Shipments.SHIPMENTS_PICKING_LIST,
            hidden: !checkAccessByPolicies(
              [Policies.WARHOUSE.ADMIN_ACCESS],
              [Policies.WARHOUSE.ASSIGN_ORDER_TO_PICKERS]
            ),
          },
          {
            label: "Picker's list",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Shipments.SHIPMENTS_PICKER_LIST
            ),
            route: AppRoutes.Private.Shipments.SHIPMENTS_PICKER_LIST,
            hidden: !checkAccessByPolicies(
              [Policies.WARHOUSE.ADMIN_ACCESS],
              [Policies.WARHOUSE.VIEW_ORDER_TO_BE_PICKED]
            ),
          },
          {
            label: "Dispatch list",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Shipments.SHIPMENTS_DESPATCH_LIST
            ),
            route: AppRoutes.Private.Shipments.SHIPMENTS_DESPATCH_LIST,
            hidden: !checkAccessByPolicies(
              [Policies.WARHOUSE.ADMIN_ACCESS],
              [Policies.WARHOUSE.BOOK_CONSIGNMENTS]
            ),
          },
          {
            label: "Manifest history",
            icon: <Dot name="dot" />,
            isActive: location.pathname.includes(
              AppRoutes.Private.Shipments.SHIPMENTS_MANIFEST_HISTORY
            ),
            route: AppRoutes.Private.Shipments.SHIPMENTS_MANIFEST_HISTORY,
            hidden: !checkAccessByPolicies(
              [Policies.WARHOUSE.ADMIN_ACCESS],
              [Policies.WARHOUSE.GENERATE_AND_PRINT_MANIFEST]
            ),
          },
        ],
      },
    ];

    const enabled = items.filter((item) => !item.disabled);
    const disabled = items.filter((item) => item.disabled);

    return [...enabled, ...disabled];
  }, [
    location.pathname,
    cardPaymentsEnabled,
    hasB2bSubscription,
    company,
    canManageCompanies,
    hasNoSubscription,
    hasPaymentsOnlySubscription,
    hasActiveB2BSubscription,
    hasToolkitSubscription,
  ]);
  const settingsMenuItems = useMemo(
    () =>
      [
        {
          label: "Business account",
          isActive:
            location.pathname === AppRoutes.Private.Settings.BUSINESS_ACCOUNT,
          route: AppRoutes.Private.Settings.BUSINESS_ACCOUNT,
          hidden: !checkAccessByPolicies(
            [Policies.COMPANY.ADMIN_ACCESS],
            [Policies.COMPANY.ACCESS_COMPANY_SETTINGS]
          ),
        },
        {
          label: "Verifications",
          isActive:
            location.pathname === AppRoutes.Private.Settings.VERIFICATION,
          route: AppRoutes.Private.Settings.VERIFICATION,
          hidden: !checkAccessByPolicies(
            [Policies.COMPANY.ADMIN_ACCESS],
            [Policies.COMPANY.MANAGE_KYC_STATUS]
          ),
        },
        {
          label: "Bank accounts",
          isActive:
            location.pathname === AppRoutes.Private.Settings.BANK_ACCOUNTS,
          route: AppRoutes.Private.Settings.BANK_ACCOUNTS,
          hidden: !checkAccessByPolicies(
            [Policies.COMPANY.ADMIN_ACCESS],
            [Policies.COMPANY.MANAGE_BANK_ACCOUNTS]
          ),
        },
        {
          label: "Currency",
          isActive: location.pathname === AppRoutes.Private.Settings.CURRENCY,
          route: AppRoutes.Private.Settings.CURRENCY,
          hidden: !checkAccessByPolicies(
            [Policies.COMPANY.ADMIN_ACCESS],
            [Policies.COMPANY.MANAGE_COMPANY_CURRENCY_SETTINGS]
          ),
        },
        {
          label: "Team Management",
          isActive:
            location.pathname === AppRoutes.Private.Settings.TEAM_MANAGEMENT,
          route: AppRoutes.Private.Settings.TEAM_MANAGEMENT,
          hidden: !checkAccessByPolicies(
            [Policies.TEAM.ADMIN_ACCESS],
            [Policies.TEAM.VIEW_TEAM]
          ),
        },
        {
          label: "Roles",
          isActive: location.pathname === AppRoutes.Private.Settings.ROLES,
          route: AppRoutes.Private.Settings.ROLES,
          hidden: !checkAccessByPolicies(
            [Policies.ROLE.ADMIN_ACCESS],
            [Policies.ROLE.VIEW_ROLES]
          ),
        },
        {
          label: "API Keys",
          isActive: location.pathname === AppRoutes.Private.Settings.API_KEYS,
          route: AppRoutes.Private.Settings.API_KEYS,
          hidden: !checkAccessByPolicies(
            [Policies.API_KEY.ADMIN_ACCESS],
            [Policies.API_KEY.VIEW_API_KEYS]
          ),
        },
        // {
        //   label: "Pick-up locations",
        //   isActive:
        //     location.pathname === AppRoutes.Private.Settings.PICK_UP_LOCATIONS,
        //   route: AppRoutes.Private.Settings.PICK_UP_LOCATIONS,
        //   hidden: !checkAccessByPolicies(
        //     [Policies.LOCATION.ADMIN_ACCESS],
        //     [Policies.LOCATION.VIEW_LOCATIONS]
        //   ),
        // },
        {
          label: "Integrations",
          isActive: location.pathname.includes("private/settings/integrations"),
          route: AppRoutes.Private.Settings.PICK_UP_LOCATIONS,
          defaultOpen: window.location.pathname.includes(
            "private/settings/integrations"
          ),
          hidden: !checkAccessByPolicies(
            [
              Policies.INTEGRATIONS.ADMIN_ACCESS,
              Policies.INTEGRATIONS.VIEW_INTEGRATIONS,
              Policies.WARHOUSE.ADMIN_ACCESS,
              Policies.WARHOUSE.ACCESS_SHIPPING_SETTINGS,
            ],
            [
              Policies.INTEGRATIONS.ADMIN_ACCESS,
              Policies.INTEGRATIONS.VIEW_INTEGRATIONS,
              Policies.WARHOUSE.ADMIN_ACCESS,
              Policies.WARHOUSE.ACCESS_SHIPPING_SETTINGS,
            ]
          ),
          subItems: [
            {
              label: "Accounting",
              icon: <Dot name="dot" />,
              isActive: location.pathname.includes(
                AppRoutes.Private.Settings.INTEGRATIONS_ACCOUNTING
              ),
              route: AppRoutes.Private.Settings.INTEGRATIONS_ACCOUNTING,
              hidden: !checkAccessByPolicies(
                [Policies.INTEGRATIONS.ADMIN_ACCESS],
                [Policies.INTEGRATIONS.VIEW_INTEGRATIONS]
              ),
            },
            {
              label: "eCommerce stores",
              icon: <Dot name="dot" />,
              isActive: location.pathname.includes(
                AppRoutes.Private.Settings.INTEGRATIONS_ECOMMERCE
              ),
              route: AppRoutes.Private.Settings.INTEGRATIONS_ECOMMERCE,
              hidden: !checkAccessByPolicies(
                [Policies.INTEGRATIONS.ADMIN_ACCESS],
                [Policies.INTEGRATIONS.VIEW_INTEGRATIONS]
              ),
            },
            {
              label: "Marketplaces",
              icon: <Dot name="dot" />,
              isActive: location.pathname.includes(
                AppRoutes.Private.Settings.INTEGRATIONS_MARKETPLACES
              ),
              route: AppRoutes.Private.Settings.INTEGRATIONS_MARKETPLACES,
              hidden: !checkAccessByPolicies(
                [Policies.INTEGRATIONS.ADMIN_ACCESS],
                [Policies.INTEGRATIONS.VIEW_INTEGRATIONS]
              ),
            },
            {
              label: "Warehouse management",
              icon: <Dot name="dot" />,
              isActive: location.pathname.includes(
                AppRoutes.Private.Settings.INTEGRATIONS_WAREHOUSE
              ),
              route: AppRoutes.Private.Settings.INTEGRATIONS_WAREHOUSE,
              hidden: !checkAccessByPolicies(
                [Policies.INTEGRATIONS.ADMIN_ACCESS],
                [Policies.INTEGRATIONS.VIEW_INTEGRATIONS]
              ),
            },
            {
              label: "Couriers",
              icon: <Dot name="dot" />,
              isActive: location.pathname.includes(
                AppRoutes.Private.Settings.INTEGRATIONS_CARRIERS
              ),
              route: AppRoutes.Private.Settings.INTEGRATIONS_CARRIERS,
              hidden: !checkAccessByPolicies(
                [Policies.WARHOUSE.ADMIN_ACCESS],
                [Policies.WARHOUSE.ACCESS_SHIPPING_SETTINGS]
              ),
            },
            {
              label: "Payments",
              icon: <Dot name="dot" />,
              isActive: location.pathname.includes(
                AppRoutes.Private.Settings.INTEGRATIONS_PAYMENTS
              ),
              route: AppRoutes.Private.Settings.INTEGRATIONS_PAYMENTS,
              // hidden: !checkAccessByPolicies([Policies.WARHOUSE.ADMIN_ACCESS]),
            },
            {
              label: "Other Apps",
              icon: <Dot name="dot" />,
              isActive: location.pathname.includes(
                AppRoutes.Private.Settings.INTEGRATIONS_OTHERS
              ),
              route: AppRoutes.Private.Settings.INTEGRATIONS_OTHERS,
              hidden: !checkAccessByPolicies(
                [Policies.INTEGRATIONS.ADMIN_ACCESS],
                [Policies.INTEGRATIONS.VIEW_INTEGRATIONS]
              ),
            },
          ],
        },
        {
          label: "Apps",
          isActive: location.pathname.includes("/private/settings/apps"),
          route: AppRoutes.Private.Settings.INTEGRATIONS_APPS,
          hidden:
            !!company?.managedBy ||
            !checkAccessByPolicies(
              [Policies.APPS.ADMIN_ACCESS],
              [Policies.APPS.VIEW_APPS]
            ),
        },
        {
          label: "Commercial Invoices",
          isActive:
            location.pathname ===
            AppRoutes.Private.Settings.COMMERCIAL_INVOICES,
          route: AppRoutes.Private.Settings.COMMERCIAL_INVOICES,
          hidden: !checkAccessByPolicies(
            [Policies.COMMERCIAL_INVOICES.ADMIN_ACCESS],
            [Policies.COMMERCIAL_INVOICES.VIEW_COMMERCIAL_INVOICES]
          ),
        },
        {
          label: "Customisation",
          isActive: window.location.pathname.includes(
            "private/settings/customisation"
          ),
          defaultOpen: window.location.pathname.includes(
            "private/settings/customisation"
          ),

          hidden: !checkAccessByPolicies(
            [Policies.COMPANY.ADMIN_ACCESS],
            [Policies.COMPANY.MANAGE_COMPANY_CUSTOMIZATION_SETTINGS]
          ),
          subItems: [
            {
              label: "Email notifications",
              icon: <Dot name="dot" />,
              isActive: window.location.pathname.includes(
                AppRoutes.Private.Settings.CUSTOMISATION_EMAIL_NOTIFICATIONS
              ),
              route:
                AppRoutes.Private.Settings.CUSTOMISATION_EMAIL_NOTIFICATIONS,
            },
            {
              label: "Payment page",
              icon: <Dot name="dot" />,
              isActive: window.location.pathname.includes(
                AppRoutes.Private.Settings.CUSTOMISATION_PAYMENT_PAGE
              ),
              route: AppRoutes.Private.Settings.CUSTOMISATION_PAYMENT_PAGE,
              hidden: isB2bAccount,
            },
            {
              label: "Invoice design",
              icon: <Dot name="dot" />,
              isActive: window.location.pathname.includes(
                AppRoutes.Private.Settings.CUSTOMISATION_INVOICE_DESIGN
              ),
              route: AppRoutes.Private.Settings.CUSTOMISATION_INVOICE_DESIGN,
            },
            {
              label: "Fena checkout flow",
              icon: <Dot name="dot" />,
              isActive: window.location.pathname.includes(
                AppRoutes.Private.Settings.CUSTOMISATION_FENA_CHECKOUT_FLOW
              ),
              hidden: !cardPaymentsEnabled,
              route:
                AppRoutes.Private.Settings.CUSTOMISATION_FENA_CHECKOUT_FLOW,
            },
          ],
        },
        {
          label: "Locations",
          isActive:
            location.pathname === AppRoutes.Private.Settings.LOCATIONS_LIST,
          route: AppRoutes.Private.Settings.LOCATIONS_LIST,
          hidden: !checkAccessByPolicies(
            [Policies.LOCATION.ADMIN_ACCESS],
            [Policies.LOCATION.VIEW_LOCATIONS]
          ),
        },
        {
          label: "Billing and Packages",
          isActive:
            location.pathname ===
            AppRoutes.Private.Settings.BILLING_CHOOSE_PLAN,
          route: AppRoutes.Private.Settings.BILLING_CHOOSE_PLAN,
          hidden: !checkAccessByPolicies(
            [Policies.COMPANY_BILLING.ADMIN_ACCESS],
            [Policies.COMPANY_BILLING.ACCESS_COMPANY_BILLING_SETTINGS]
          ),
        },
        {
          label: "Reminders",
          isActive: window.location.pathname.includes(
            AppRoutes.Private.Settings.REMINDERS_LIST
          ),
          route: AppRoutes.Private.Settings.REMINDERS_LIST,
          hidden: !checkAccessByPolicies(
            [Policies.COMPANY.ADMIN_ACCESS],
            [Policies.COMPANY.MANAGE_COMPANY_REMINDERS_SETTINGS]
          ),
        },
        {
          label: "Terminal Management",
          isActive: window.location.pathname.includes(
            AppRoutes.Private.Settings.TERMINAL_MANAGEMENT_LIST
          ),
          route: AppRoutes.Private.Settings.TERMINAL_MANAGEMENT_LIST,
          hidden:
            !checkAccessByPolicies(
              [Policies.LOCATION.ADMIN_ACCESS],
              [Policies.LOCATION.VIEW_LOCATIONS]
            ) || isB2bAccount,
        },
        {
          label: "Segments",
          isActive: window.location.pathname.includes(
            "private/settings/segments"
          ),
          defaultOpen: window.location.pathname.includes(
            "private/settings/segments"
          ),
          hidden: !checkAccessByPolicies(
            [Policies.COMPANY.ADMIN_ACCESS],
            [Policies.COMPANY.MANAGE_SEGMENT_SETTINGS]
          ),
          subItems: [
            {
              label: "Customer segments",
              icon: <Dot name="dot" />,
              isActive: window.location.pathname.includes(
                AppRoutes.Private.Settings.SEGMENTS_CUSTOMER_SEGMENTS
              ),
              route: AppRoutes.Private.Settings.SEGMENTS_CUSTOMER_SEGMENTS,
            },
            {
              label: "Location segments",
              icon: <Dot name="dot" />,
              isActive: window.location.pathname.includes(
                AppRoutes.Private.Settings.SEGMENTS_LOCATION_SEGMENTS
              ),
              route: AppRoutes.Private.Settings.SEGMENTS_LOCATION_SEGMENTS,
            },
          ],
        },
      ]
        .filter((item) => !item.hidden)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [location.pathname, policies, cardPaymentsEnabled]
  );

  const partnerMenuItems = useMemo(
    () => [
      {
        label: "Dashboard",
        icon: <Dashboard name="dashboard" />,
        isActive: location.pathname.includes("partner/dashboard"),
        route: AppRoutes.Private.Partner.DASHBOARD,
        hidden: !checkAccessByPolicies(
          [Policies.PARTNER.ADMIN_ACCESS],
          [Policies.PARTNER.VIEW_DASHBOARD]
        ),
      },
      {
        label: "Transactions",
        icon: <Payments name="payments" />,
        isActive: location.pathname.includes("partner/transactions"),
        route: AppRoutes.Private.Partner.TRANSACTIONS_LIST,
        hidden: !checkAccessByPolicies(
          [Policies.PARTNER.ADMIN_ACCESS],
          [Policies.PARTNER.VIEW_MANAGED_COMPANIES_TRANSACTIONS]
        ),
      },
      {
        label: "Merchants",
        icon: <Customers name="customers" />,
        isActive: location.pathname.includes("partner/merchants"),
        route: AppRoutes.Private.Partner.MERCHANTS_LIST,
        hidden: !checkAccessByPolicies(
          [Policies.PARTNER.ADMIN_ACCESS],
          [Policies.PARTNER.VIEW_MANAGED_COMPANIES]
        ),
      },
    ],
    [location.pathname]
  );

  const partnerSettingsMenuItems = useMemo(
    () =>
      [
        {
          label: "Business account",
          isActive:
            location.pathname ===
            AppRoutes.Private.Partner.PARTNER_BUSINESS_ACCOUNT,
          route: AppRoutes.Private.Partner.PARTNER_BUSINESS_ACCOUNT,
          hidden: !checkAccessByPolicies(
            [Policies.COMPANY.ADMIN_ACCESS],
            [Policies.COMPANY.ACCESS_COMPANY_SETTINGS]
          ),
        },

        {
          label: "Bank accounts",
          isActive:
            location.pathname ===
            AppRoutes.Private.Partner.PARTNER_BANK_ACCOUNTS,
          route: AppRoutes.Private.Partner.PARTNER_BANK_ACCOUNTS,
          hidden: !checkAccessByPolicies(
            [Policies.COMPANY.ADMIN_ACCESS],
            [Policies.COMPANY.MANAGE_BANK_ACCOUNTS]
          ),
        },

        {
          label: "API Keys",
          isActive:
            location.pathname === AppRoutes.Private.Partner.PARTNER_API_KEYS,
          route: AppRoutes.Private.Partner.PARTNER_API_KEYS,
          hidden: !checkAccessByPolicies(
            [Policies.API_KEY.ADMIN_ACCESS],
            [Policies.API_KEY.VIEW_API_KEYS]
          ),
        },

        {
          label: "Customisation",
          isActive: window.location.pathname.includes(
            "private/partner/settings/customisation"
          ),
          defaultOpen: window.location.pathname.includes(
            "private/partner/settings/customisation"
          ),
          // hidden: !checkAccessByPolicies([], []),
          subItems: [
            {
              label: "Email notifications",
              icon: <Dot name="dot" />,
              isActive: window.location.pathname.includes(
                AppRoutes.Private.Partner
                  .PARTNER_CUSTOMISATION_EMAIL_NOTIFICATIONS
              ),
              route:
                AppRoutes.Private.Partner
                  .PARTNER_CUSTOMISATION_EMAIL_NOTIFICATIONS,
            },
            {
              label: "Partner dashboard",
              icon: <Dot name="dot" />,
              isActive: window.location.pathname.includes(
                AppRoutes.Private.Partner.PARTNER_DASHBOARD
              ),
              route: AppRoutes.Private.Partner.PARTNER_DASHBOARD,
            },
          ],
        },
        {
          label: "Partner settings",
          isActive:
            location.pathname ===
            AppRoutes.Private.Partner.PARTNER_WEBHOOK_SETTINGS,
          route: AppRoutes.Private.Partner.PARTNER_WEBHOOK_SETTINGS,
        },
      ]
        .filter((item) => !item.hidden)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [location.pathname, policies]
  );

  return {
    menuItems,
    settingsMenuItems,
    partnerMenuItems,
    partnerSettingsMenuItems,
  };
};
